/**
 * Progress Bar
 *
 * Scroll-based progress bar tracking reading progress through content.
 * Uses GSAP ScrollTrigger to animate bar width from 0% to 100%.
 *
 * Structure:
 *   <div data-hs-toc="content">
 *     <!-- Content to track -->
 *   </div>
 *   <div data-hs-toc-progress="wrapper">
 *     <div data-hs-toc-progress="element"></div>
 *   </div>
 */

import { querySelector, querySelectorAll, getGsap, prefersReducedMotion } from '@utils';

interface ProgressBarConfig {
  progressConfig: {
    attributes: {
      elements: {
        wrapper: { primary: string; aliases?: string[] };
        element: { primary: string; aliases?: string[] };
      };
    };
  };
  tocConfig: {
    attributes: {
      elements: {
        content: { primary: string; aliases?: string[] };
      };
    };
  };
}

export async function init(configObj: ProgressBarConfig) {
  const { progressConfig: config, tocConfig } = configObj;

  // Skip if user prefers reduced motion
  if (prefersReducedMotion()) {
    return { result: 'progress-bar skipped - prefers-reduced-motion enabled' };
  }

  const gsapLib = getGsap('progress-bar');
  if (!gsapLib) {
    return { result: 'progress-bar skipped - GSAP not loaded' };
  }

  const { gsap, ScrollTrigger } = gsapLib;

  if (!ScrollTrigger) {
    console.warn('[progress-bar] GSAP ScrollTrigger plugin not loaded');
    return { result: 'progress-bar skipped - ScrollTrigger not loaded' };
  }

  gsap.registerPlugin(ScrollTrigger);

  const cleanup = {
    scrollTriggers: [] as unknown[],
  };

  // Find content area (using toc config since they share the same content)
  const content = querySelector(tocConfig, 'content');
  if (!content) {
    return { result: 'progress-bar skipped - no content element found' };
  }

  // Find all progress bar wrappers
  const wrappers = querySelectorAll(config, 'wrapper');
  if (wrappers.length === 0) {
    return { result: 'progress-bar skipped - no wrapper elements found' };
  }

  wrappers.forEach((wrapper) => {
    // Show wrapper
    gsap.set(wrapper, { display: 'block' });

    // Find progress bar elements inside this wrapper using config
    const progressBars = Array.from(wrapper.querySelectorAll('*')).filter((el) => {
      // Check primary selector
      const primaryAttr = config.attributes.elements.element.primary;
      const [attr, value] = primaryAttr.split('=');
      const cleanValue = value?.replace(/['"]/g, '');
      return el.getAttribute(attr) === cleanValue;
    });

    progressBars.forEach((bar) => {
      // Set initial width to 0%
      gsap.set(bar, { width: '0%' });

      // Animate from 0% to 100% based on scroll
      gsap.to(bar, {
        width: '100%',
        ease: 'none',
        scrollTrigger: {
          trigger: content,
          start: 'top bottom', // Start when top of content touches bottom of viewport
          end: 'bottom 75%', // End when bottom of content reaches 75% down the viewport
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            // Store the scrollTrigger instance for cleanup
            if (!cleanup.scrollTriggers.includes(self)) {
              cleanup.scrollTriggers.push(self);
            }
          },
        },
      });
    });
  });

  return {
    result: `progress-bar initialized (${wrappers.length} instances)`,
    destroy: () => {
      // Kill all scroll triggers
      cleanup.scrollTriggers.forEach((st) => {
        if (st && typeof st === 'object' && 'kill' in st && typeof st.kill === 'function') {
          st.kill();
        }
      });
      cleanup.scrollTriggers.length = 0;
    },
  };
}
